import {observer} from 'mobx-react';
import {withState, t} from 'shared/core';
import {endpoints, types} from 'main/tools';
import {Layout, Panel, ObjectSelect2} from 'shared/components';
import {InteractiveAccordionTable} from 'main/components';
import {PayrollHoursContainerState} from '../state';
import {ReportDownloadButton} from '../../download';
import {useModal} from 'shared/hooks';
import ErrorMessageModal from './ErrorMessageModal';
import _ from 'lodash';
import {TimeTrackingPayrollHoursSummary} from 'main/stores/time_tracking';
import {
  EmployeeNameCell,
  OvertimeCell,
  DoubleOvertimeCell,
  ApproveButtonCell,
  ProjectNameCell,
  ProjectOvertimeCell,
  ProjectDoubleOvertimeCell
} from './cells';
import ProjectPTOCell from 'main/containers/time_tracking/components/payroll_hours/components/cells/ProjectPTOCell';

const PAYROLL_HOURS_COLUMNS = [
  {
    header: 'time_tracking.payroll_hours.Employee Name',
    width: 3,
    component: EmployeeNameCell
  },
  {
    header: 'time_tracking.payroll_hours.Policy',
    width: 2,
    attribute: 'policy.name'
  },
  {
    header: 'time_tracking.payroll_hours.Regular',
    width: 1,
    attribute: 'regular'
  },
  {
    header: 'time_tracking.payroll_hours.Overtime',
    width: 1.5,
    component: OvertimeCell
  },
  {
    header: 'time_tracking.payroll_hours.Double Overtime',
    width: 1.5,
    component: DoubleOvertimeCell
  },
  {
    header: 'time_tracking.payroll_hours.Holiday Worked',
    width: 1.5,
    attribute: 'holidaysTotal'
  },
  {
    header: 'time_tracking.payroll_hours.Paid Time Off',
    width: 1.5,
    attribute: 'ptoTotal'
  },
  {
    header: 'time_tracking.payroll_hours.Total',
    width: 1,
    attribute: 'total'
  },
  {
    width: 1,
    component: ApproveButtonCell,
    className: 'justify-content-end'
  }
];

const PROJECT_COLUMNS = [
  {
    width: 5,
    component: ProjectNameCell
  },
  {
    width: 1,
    attribute: 'regular'
  },
  {
    width: 1.5,
    component: ProjectOvertimeCell
  },
  {
    width: 1.5,
    component: ProjectDoubleOvertimeCell
  },
  {
    width: 1.5,
    attribute: 'holiday'
  },
  {
    width: 1.5,
    component: ProjectPTOCell
  },
  {
    width: 1,
    attribute: 'total'
  }
];

const PayrollHoursContainerWithState = observer(({uiState}) => {
  const {paySchedules, selectedPaySchedule, selectedPayPeriod} = uiState;
  const errorMessageModalOpen = useModal(uiState, 'ErrorMessageModal', 'selectedPayrollHoursSummary');

  if (_.isEmpty(paySchedules)) return null;

  return (
    <Layout>
      <Panel>
        <Panel.Header title={t('time_tracking.payroll_hours.Payroll Hours')}/>
        <div className='flex flex-wrap mb2 justify-content-between align-items-end'>
          <div className='flex'>
            <div className='mr1'>
              <ObjectSelect2
                label='time_tracking.payroll_hours.Pay Schedule'
                value={selectedPaySchedule}
                onChange={value => uiState.selectPaySchedule(value)}
                items={paySchedules}
              />
            </div>
            <ObjectSelect2
              label='time_tracking.payroll_hours.Pay Period'
              value={selectedPayPeriod}
              onChange={value => uiState.selectPayPeriod(value)}
              items={selectedPaySchedule.sortedPayPeriods}
              display='effectiveDateView'
            />
          </div>
          <ReportDownloadButton
            reportType='payroll_hours'
            name={t('time_tracking.payroll_hours.Payroll Hours')}
            params={{
              payScheduleId: selectedPaySchedule.id, payPeriodId: selectedPayPeriod.id
            }}
          />
        </div>
        <InteractiveAccordionTable
          onMount={agent => uiState.setInteractiveAgent(agent)}
          proxy={{
            type: 'async',
            pageSize: 10,
            modelType: types.TIME_TRACKING.PAYROLL_HOURS_SUMMARY,
            model: TimeTrackingPayrollHoursSummary,
            endpoint: endpoints.TIME_TRACKING.PAYROLL_HOURS.with(selectedPaySchedule.id, selectedPayPeriod.id)
          }}
          columns={PAYROLL_HOURS_COLUMNS}
          nestedRows={uiState.showAccordion ? [{attribute: 'sortedProjects', columns: PROJECT_COLUMNS}] : []}
          canExpand={(model) => parseInt(model.total) > 0 && parseInt(model.total) > parseInt(model.ptoTotal)}
          onExpand={(model) => { model.sortedProjects = uiState.sortedProjects(model); }}
          showLinks={false}
          searchable={false}
          sortable={false}
          emptyStateMessage='time_tracking.payroll_hours.No employees to display'
          uiState={uiState}
          wrapInPanel={false}
          horizontalScroll
        />
      </Panel>
      <ErrorMessageModal modalOpen={errorMessageModalOpen} uiState={uiState}/>
    </Layout>
  );
});

export default withState(PayrollHoursContainerWithState, PayrollHoursContainerState);
